import React, { useState, useEffect, useCallback } from "react";
import { Segment, Label, Icon, Statistic, Grid, Responsive, Tab, TabProps, Divider, Button, Embed, Image } from "semantic-ui-react";

import DynamicRepository from "../../Repositories/DynamicRepository";
import { format } from "date-fns";
import TemplateMain from "../../components/TemplateMain";
import { Link } from "react-router-dom";
import Schedule from "./Schedule/Index";
import ImageZoom from "../../components/ImageZoom";

interface ITotalPessoa {
    Ativos: number;
    Inativos: number;
    Pendentes: number;
    Devendo: number;
}

interface ITotalKits {
    GerenteSetor: number;
    Representante: number;
    Revendedor: number;
}

interface State {
    representante: ITotalPessoa;
    revendedor: ITotalPessoa;
    kits: ITotalKits;
}

const GerenteSetorItems: React.FC = () => {

    const [state, setState] = useState<State>();
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

    const loadTotalGeral = useCallback(async () => {
        setLoading(true);

        const response = await DynamicRepository.DynamicTotals([
            {
                dynamicType: "RP",
                returnCode: "RPATI",
                filter: `Status == 1 && Devedor == 0`
            },
            {
                dynamicType: "RP",
                returnCode: "RPINA",
                filter: `Status == 0 && Devedor == 0`
            },
            {
                dynamicType: "RP",
                returnCode: "RPPEN",
                filter: `Status == 2 && Devedor == 0`
            },
            {
                dynamicType: "RP",
                returnCode: "RPDEV",
                filter: `Devedor == 1`
            },
            {
                dynamicType: "RV",
                returnCode: "RVATI",
                filter: `Status == 1 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVINA",
                filter: `Status == 0 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVPEN",
                filter: `Status == 2 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVDEV",
                filter: `Devedor == 1`
            },
            {
                dynamicType: "KT",
                returnCode: "KTGES",
                filter: `StatusKit == 1`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREP",
                filter: `StatusKit == 2`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREV",
                filter: "StatusKit == 3 && Tipo == 0"
            },
        ]);

        setState({
            representante: {
                Ativos: response.RPATI,
                Inativos: response.RPINA,
                Pendentes: response.RPPEN,
                Devendo: response.RPDEV
            },
            revendedor: {
                Ativos: response.RVATI,
                Inativos: response.RVINA,
                Pendentes: response.RVPEN,
                Devendo: response.RVDEV
            },
            kits: {
                GerenteSetor: response.KTGES,
                Representante: response.KTREP,
                Revendedor: response.KTREV
            }
        });

        setLoading(false);
    }, []);

    const loadTotalMensal = useCallback(async () => {
        setLoading(true);

        const response = await DynamicRepository.DynamicTotals([
            {
                dynamicType: "RP",
                returnCode: "RPATI",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 1 && Devedor == 0`
            },
            {
                dynamicType: "RP",
                returnCode: "RPINA",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 0 && Devedor == 0`
            },
            {
                dynamicType: "RP",
                returnCode: "RPPEN",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 2 && Devedor == 0`
            },
            {
                dynamicType: "RP",
                returnCode: "RPDEV",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Devedor == 1`
            },
            {
                dynamicType: "RV",
                returnCode: "RVATI",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 1 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVINA",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 0 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVPEN",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Status == 2 && Devedor == 0`
            },
            {
                dynamicType: "RV",
                returnCode: "RVDEV",
                filter: `Data >= "${format(new Date(), "yyyy-MM-01")}" && Devedor == 1`
            },
            {
                dynamicType: "KT",
                returnCode: "KTGES",
                filter: `StatusKit == 1`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREP",
                filter: `StatusKit == 2`
            },
            {
                dynamicType: "KT",
                returnCode: "KTREV",
                filter: `DataEntrega >= "${format(new Date(), "yyyy-MM-01")}" && StatusKit == 3 && Tipo == 0`
            },
        ]);

        setState({
            representante: {
                Ativos: response.RPATI,
                Inativos: response.RPINA,
                Pendentes: response.RPPEN,
                Devendo: response.RPDEV
            },
            revendedor: {
                Ativos: response.RVATI,
                Inativos: response.RVINA,
                Pendentes: response.RVPEN,
                Devendo: response.RVDEV
            },
            kits: {
                GerenteSetor: response.KTGES,
                Representante: response.KTREP,
                Revendedor: response.KTREV
            }
        });

        setLoading(false);
    }, []);

    const handleTabChange = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>, { activeIndex }: TabProps) => {
        setActiveTabIndex(activeIndex as number);
    }, []);

    useEffect(() => {
        if (activeTabIndex === 0) {
            loadTotalMensal();
        }
        else if (activeTabIndex === 1) {
            loadTotalGeral();
        }
    }, [activeTabIndex, loadTotalGeral, loadTotalMensal]);


    const renderKit = (
        <>
            <Statistic>
                <Statistic.Value>
                    {state?.kits.GerenteSetor || 0}
                </Statistic.Value>
                <Statistic.Label>
                    <Icon name="user" color="pink" />
                    {" "}Ger. Setor
                </Statistic.Label>
            </Statistic>
            <Statistic>
                <Statistic.Value>
                    {state?.kits.Representante || 0}
                </Statistic.Value>
                <Statistic.Label>
                    <Icon name="id badge" color="violet" />
                    {" "}Representante
                </Statistic.Label>
            </Statistic>
            <Statistic as={Link} to={"/agentproductivitymonth"} >
                <Statistic.Value>
                    {state?.kits.Revendedor || 0}
                </Statistic.Value>
                <Statistic.Label>
                    <Icon name="street view" color="teal" />
                    {" "}Kit's Novos
                </Statistic.Label>
            </Statistic>
        </>
    );

    const content = (
        <Grid columns="2" doubling>
            <Grid.Row>
                <Grid.Column width="16">
                    <Tab
                        menu={{ secondary: true, pointing: true }}
                        panes={[{ menuItem: "Mensal" }, { menuItem: "Geral" }, { menuItem: "Calendário" }]}
                        activeIndex={activeTabIndex}
                        onTabChange={handleTabChange}
                    />
                </Grid.Column>
            </Grid.Row>
            
            {(activeTabIndex === 0 || activeTabIndex === 1) &&
                <>
                    <Grid.Row>
                        <Grid.Column width="16">
                            <Segment color="purple" raised>
                                <Label ribbon content="Kits" size="large" color="purple" />
                                <Responsive as={Statistic.Group} {...Responsive.onlyComputer} widths="3" size="small">
                                    {renderKit}
                                </Responsive>
                                <Responsive as={Statistic.Group} {...Responsive.onlyMobile} widths="2" size="small">
                                    {renderKit}
                                </Responsive>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                        <Grid.Column>
                            <Segment color="pink" raised>
                                <Label ribbon content="Representantes" size="large" color="pink" />
                                <Statistic.Group widths="two" size="small">
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.representante.Ativos || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="check" color="pink" />
                                            {" "}Ativos
                                        </Statistic.Label>
                                    </Statistic>
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.representante.Inativos || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="times" color="grey" />
                                            {" "}Inativos
                                        </Statistic.Label>
                                    </Statistic>
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.representante.Pendentes || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="clock" color="orange" />
                                            {" "}Pendentes
                                        </Statistic.Label>
                                    </Statistic>
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.representante.Devendo || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="user times" color="red" />
                                            {" "}Devendo
                                        </Statistic.Label>
                                    </Statistic>
                                </Statistic.Group>
                            </Segment>
                        </Grid.Column>
                        <Grid.Column>
                            <Segment color="pink" raised>
                                <Label ribbon content="Revendedores" size="large" color="pink" />
                                <Statistic.Group widths="two" size="small">
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.revendedor.Ativos || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="check" color="pink" />
                                            {" "}Ativos
                                        </Statistic.Label>
                                    </Statistic>
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.revendedor.Inativos || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="times" color="grey" />
                                            {" "}Inativos
                                        </Statistic.Label>
                                    </Statistic>
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.revendedor.Pendentes || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="clock" color="orange" />
                                            {" "}Pendentes
                                        </Statistic.Label>
                                    </Statistic>
                                    <Statistic>
                                        <Statistic.Value>
                                            {state?.revendedor.Devendo || 0}
                                        </Statistic.Value>
                                        <Statistic.Label>
                                            <Icon name="user times" color="red" />
                                            {" "}Devendo
                                        </Statistic.Label>
                                    </Statistic>
                                </Statistic.Group>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </>}
            {activeTabIndex === 2 &&
                <Schedule isAdmin={false} />
            }
        </Grid>
    );

    return (
        <TemplateMain
            loading={loading}
            content={content}
        />
    );
};

export default GerenteSetorItems;
